import { useState } from 'react';
import { mockUserProfile } from '@/data/mockData';

export function Profile() {
  const [profile] = useState(mockUserProfile);
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">My Profile</h1>
            <p className="text-xl text-gray-600">Manage your skin profile and preferences</p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Sidebar */}
            <aside className="lg:col-span-1">
              <div className="bg-white rounded-2xl border border-gray-200 p-6 sticky top-24">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-violet-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">👤</span>
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">{profile.name}</h2>
                  <p className="text-gray-600">{profile.email}</p>
                </div>
                
                <nav className="space-y-2">
                  <button className="w-full text-left px-3 py-2 bg-violet-100 text-violet-700 rounded-lg font-medium">
                    Profile Details
                  </button>
                  <button className="w-full text-left px-3 py-2 hover:bg-gray-100 text-gray-700 rounded-lg">
                    Analysis History
                  </button>
                  <button className="w-full text-left px-3 py-2 hover:bg-gray-100 text-gray-700 rounded-lg">
                    Orders
                  </button>
                  <button className="w-full text-left px-3 py-2 hover:bg-gray-100 text-gray-700 rounded-lg">
                    Notifications
                  </button>
                  <button className="w-full text-left px-3 py-2 hover:bg-gray-100 text-gray-700 rounded-lg">
                    Privacy Settings
                  </button>
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <main className="lg:col-span-2">
              <div className="bg-white rounded-2xl border border-gray-200 p-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Profile Information</h3>
                
                <div className="space-y-8">
                  {/* Basic Information */}
                  <section>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                        <input
                          type="text"
                          value={profile.name}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                          disabled
                        />
                        <p className="text-xs text-gray-500 mt-1">Contact support to change your name</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                        <input
                          type="email"
                          value={profile.email}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                          disabled
                        />
                        <p className="text-xs text-gray-500 mt-1">Contact support to change your email</p>
                      </div>
                    </div>
                  </section>

                  {/* Skin Profile */}
                  <section>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Skin Profile</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Skin Type</label>
                        <input
                          type="text"
                          value={profile.skinType}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                          disabled
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Environment</label>
                        <input
                          type="text"
                          value={profile.environment}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                          disabled
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Budget Range</label>
                        <input
                          type="text"
                          value={profile.budget}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                          disabled
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Age Range</label>
                        <input
                          type="text"
                          value={profile.ageRange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                          disabled
                        />
                      </div>
                    </div>
                  </section>

                  {/* Skin Goals */}
                  <section>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Skin Goals</h4>
                    <div className="flex flex-wrap gap-2">
                      {profile.goals.map((goal) => (
                        <span key={goal} className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-sm">
                          {goal}
                        </span>
                      ))}
                    </div>
                  </section>

                  {/* Allergies & Sensitivities */}
                  <section>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Allergies & Sensitivities</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Allergies</label>
                        <div className="flex flex-wrap gap-2">
                          {profile.allergies.length > 0 ? (
                            profile.allergies.map((allergy) => (
                              <span key={allergy} className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                                {allergy}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-gray-500">No known allergies</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Sensitivities</label>
                        <div className="flex flex-wrap gap-2">
                          {profile.sensitivities.length > 0 ? (
                            profile.sensitivities.map((sensitivity) => (
                              <span key={sensitivity} className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm">
                                {sensitivity}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-gray-500">No known sensitivities</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Lifestyle */}
                  <section>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Lifestyle</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Lifestyle Factors</label>
                      <input
                        type="text"
                        value={profile.lifestyle}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                        disabled
                      />
                    </div>
                  </section>

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                    <button className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                      Export My Data
                    </button>
                    <button className="px-6 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-lg hover:shadow-lg transition-all">
                      Update Profile
                    </button>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}